import { useEffect, useMemo, useState } from 'react';
import { resolveIntroLinksFromAnchorLinks } from '../../../lib/briefModel.js';
import { aiClient, AiBudgetExhaustedError } from './lib/aiClient.js';
import {
  CATEGORY_ORDER,
  CATEGORY_PALETTE,
  CATEGORY_FALLBACK,
  categoryRank,
} from './lib/categoryPalette.js';

// Final Check step 2 of 4 — Red Thread rewrite.
//
// Anchor-first flow: the editor picks 3 to 5 articles to anchor the day's
// thesis, then clicks Generate. The active editor_red_thread prompt
// synthesizes the red thread around those anchors (this is the editor-time
// counterpart to the pipeline's stage_4_b_red_thread — managed separately
// in the dev dashboard so editor rewrites can diverge from pipeline drafts).
// No auto-draft on
// mount — the editor's selection drives the AI.
//
// Articles render as a collapsible reference list above the red thread so
// the picking step is the first thing the editor lands on. Edits to the
// thesis are kept after Regenerate; the editor's anchor selection is also
// preserved across regenerations (we don't clobber it with the AI's pick).
const MIN_ANCHORS = 3;
const MAX_ANCHORS = 5;

export default function FinalCheck({
  isoDate,
  edition,
  onIntroChange,
  onIntroRedThreadChange,
  onBack,
  onPreview,
}) {
  const initialThesis = edition?.intro?.summary || '';
  const [thesis, setThesis] = useState(initialThesis);
  const [edited, setEdited] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState(null);
  const [meta, setMeta] = useState(null); // { promptName, promptVersion, model, latencyMs, threadQuality, anchorUrls, priorIsoDate }
  // Editor-controlled set of anchor source URLs. The editor must pick 3–5
  // before Generate is enabled. We send this set with every (re)generation
  // and we do NOT overwrite it with whatever the AI returns — the editor
  // stays in control.
  const [anchorUrls, setAnchorUrls] = useState(
    () => new Set(edition?.intro?.anchorUrls || [])
  );

  useEffect(() => {
    const saved = edition?.intro?.anchorUrls;
    if (Array.isArray(saved) && saved.length > 0) {
      setAnchorUrls(new Set(saved));
    }
  }, [edition?.isoDate, edition?.intro?.anchorUrls]);

  // Sort + group articles by category exactly like the main edit screen so the
  // reference list reads the same way the email will.
  const shipping = useMemo(() => {
    // Everything in the edition is fair game for anchoring — pipeline status
    // is informational, and the editor cuts anything they don't want shipping
    // before they get to Final Check.
    const list = (edition?.articles || []).filter((a) => !!a);
    return [...list].sort((a, b) => {
      const catDelta = categoryRank(a.category) - categoryRank(b.category);
      if (catDelta !== 0) return catDelta;
      return (a.order || 0) - (b.order || 0);
    });
  }, [edition]);

  // Articles whose source URL the editor has currently flagged as an anchor.
  // Wrapped in useMemo so children only re-render when the set identity flips.
  const activeAnchorSet = useMemo(() => anchorUrls, [anchorUrls]);

  const linksStale = useMemo(() => {
    const saved = edition?.intro?.links || [];
    if (!edited || saved.length === 0) return false;
    const t = thesis || '';
    return saved.some((l) => l.phrase && !t.includes(l.phrase));
  }, [edited, thesis, edition?.intro?.links]);

  function toggleAnchor(url) {
    if (!url) return;
    setAnchorUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  }

  // No auto-draft on mount: the editor's anchor picks drive the AI. If a
  // thesis already exists on the edition (e.g. they returned to this screen
  // after a prior session) we render it as-is and let them edit or regenerate.

  async function runDraft() {
    if (drafting) return;
    const selected = Array.from(anchorUrls);
    if (selected.length < MIN_ANCHORS || selected.length > MAX_ANCHORS) {
      setDraftError(
        `Pick between ${MIN_ANCHORS} and ${MAX_ANCHORS} anchor articles before generating.`
      );
      return;
    }
    setDrafting(true);
    setDraftError(null);
    try {
      const res = await aiClient.redThread({ isoDate, anchorUrls: selected });
      if (res?.redThread) {
        setThesis(res.redThread);
        setEdited(false);
        const links = resolveIntroLinksFromAnchorLinks(
          edition?.articles || [],
          res.anchorLinks
        );
        onIntroRedThreadChange?.({
          summary: res.redThread,
          anchorUrls: selected,
          links,
        });
      }
      // We intentionally do NOT overwrite the editor's anchor set with the AI's
      // returned `anchor_urls`. The editor is the source of truth; the AI's
      // pick lives on `meta.anchorUrls` for reference only.
      setMeta({
        promptName: res?.promptName,
        promptVersion: res?.promptVersion,
        model: res?.model,
        latencyMs: res?.latencyMs,
        threadQuality: res?.threadQuality,
        anchorUrls: res?.anchorUrls || [],
        priorIsoDate: res?.priorIsoDate,
      });
    } catch (err) {
      if (err instanceof AiBudgetExhaustedError) {
        // The global AI-budget modal handles this — don't surface twice.
      } else {
        setDraftError(err.message || 'Red-thread draft failed.');
      }
    } finally {
      setDrafting(false);
    }
  }

  function handleThesisChange(e) {
    const value = e.target.value;
    setThesis(value);
    setEdited(true);
    onIntroChange?.(value);
  }

  return (
    <div className="space-y-6 max-w-[860px]">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange mb-1">
            Step 2 of 4 · Final Check
          </div>
          <h1 className="hxd-page-title">Red Thread</h1>
          <p className="hxd-page-subtitle">
            Synthesize today's articles into a thesis in John's voice.
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="hxd-btn hxd-btn-ghost hxd-btn-sm"
        >
          ← Back to edit
        </button>
      </div>

      <ArticleReference
        articles={shipping}
        anchorUrls={activeAnchorSet}
        onToggleAnchor={toggleAnchor}
        disabled={drafting}
        minAnchors={MIN_ANCHORS}
        maxAnchors={MAX_ANCHORS}
      />

      <RedThreadCard
        thesis={thesis}
        edited={edited}
        drafting={drafting}
        draftError={draftError}
        meta={meta}
        articleCount={shipping.length}
        anchorCount={activeAnchorSet.size}
        minAnchors={MIN_ANCHORS}
        maxAnchors={MAX_ANCHORS}
        linksStale={linksStale}
        onChange={handleThesisChange}
        onGenerate={() => runDraft()}
      />

      <div className="sticky bottom-0 z-30 -mx-4 sm:-mx-6 mt-2 px-4 sm:px-6 py-3 bg-black/95 backdrop-blur border-t border-[#333] flex items-center gap-3">
        <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-muted">
          Step 2 of 4
        </div>
        <div className="ml-auto flex items-center gap-3">
          {drafting ? (
            <span className="text-hxd-orange-bright text-[12px]">
              Drafting red thread…
            </span>
          ) : (
            <span className="text-hxd-muted text-[12px]">
              {edited ? 'Edited by you' : 'Ready to review'}
            </span>
          )}
          <button
            type="button"
            onClick={onPreview}
            className="hxd-btn hxd-btn-primary hxd-btn-sm"
          >
            Assign images →
          </button>
        </div>
      </div>
    </div>
  );
}

function RedThreadCard({
  thesis,
  edited,
  drafting,
  draftError,
  meta,
  articleCount,
  anchorCount,
  minAnchors,
  maxAnchors,
  linksStale,
  onChange,
  onGenerate,
}) {
  const wordCount = (thesis || '').trim().split(/\s+/).filter(Boolean).length;
  const inRange = wordCount >= 60 && wordCount <= 90;

  const hasGenerated = !!thesis;
  const canGenerate = anchorCount >= minAnchors && anchorCount <= maxAnchors && !drafting;

  let anchorMessage;
  if (anchorCount === 0) {
    anchorMessage = (
      <span>
        Pick <strong>{minAnchors} to {maxAnchors}</strong> anchor articles above, then
        Generate.
      </span>
    );
  } else if (anchorCount < minAnchors) {
    const need = minAnchors - anchorCount;
    anchorMessage = (
      <span className="text-hxd-warn">
        {anchorCount} selected — pick {need} more anchor{need === 1 ? '' : 's'} to
        generate.
      </span>
    );
  } else if (anchorCount > maxAnchors) {
    const over = anchorCount - maxAnchors;
    anchorMessage = (
      <span className="text-hxd-warn">
        {anchorCount} selected — remove {over} to bring the anchor set down to
        ≤{maxAnchors}.
      </span>
    );
  } else {
    anchorMessage = (
      <span className="text-hxd-orange">
        {anchorCount} anchor{anchorCount === 1 ? '' : 's'} selected — ready to
        {hasGenerated ? ' regenerate' : ' generate'}.
      </span>
    );
  }

  const buttonLabel = drafting
    ? 'Drafting…'
    : hasGenerated
      ? 'Regenerate'
      : 'Generate red thread';

  const buttonTitle = !canGenerate
    ? `Pick ${minAnchors}–${maxAnchors} anchor articles to enable Generate`
    : hasGenerated
      ? `Regenerate using your ${anchorCount} selected anchor${anchorCount === 1 ? '' : 's'}`
      : `Generate using your ${anchorCount} selected anchor${anchorCount === 1 ? '' : 's'}`;

  return (
    <section className="hxd-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="hxd-card-header mb-0">Today's red thread</div>
        <div className="flex items-center gap-2">
          {meta?.threadQuality && (
            <ThreadQualityPill quality={meta.threadQuality} />
          )}
          <button
            type="button"
            onClick={onGenerate}
            disabled={!canGenerate}
            className={
              canGenerate
                ? 'hxd-btn hxd-btn-primary hxd-btn-sm'
                : 'hxd-btn hxd-btn-ghost hxd-btn-sm'
            }
            title={buttonTitle}
          >
            {buttonLabel}
          </button>
        </div>
      </div>

      <p className="text-[12px] text-hxd-muted mb-3">
        60–90 word paragraph in John's voice that ties today's {articleCount} stories
        into one signal. {anchorMessage}
        {meta?.promptName && (
          <>
            {' '}
            <span className="text-[11px]">
              Drafted with {meta.promptName} v{meta.promptVersion}
              {meta.priorIsoDate && (
                <> · prior thesis: {meta.priorIsoDate}</>
              )}
            </span>
          </>
        )}
      </p>

      {drafting && !thesis ? (
        <div className="border border-hxd-border-soft rounded-[3px] bg-hxd-orange-soft/40 p-6 text-center">
          <div
            aria-hidden="true"
            className="inline-block w-2.5 h-2.5 rounded-full bg-hxd-orange animate-pulse mb-2"
          />
          <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-ink mb-1">
            Wingman is drafting the red thread
          </div>
          <p className="text-[12px] text-hxd-muted">
            Reading {anchorCount} anchor article{anchorCount === 1 ? '' : 's'} and looking
            for the connective tissue.
          </p>
        </div>
      ) : !hasGenerated ? (
        <div className="border border-dashed border-hxd-border rounded-[3px] bg-hxd-orange-soft/20 p-6 text-center">
          <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-ink mb-1">
            No red thread yet
          </div>
          <p className="text-[12px] text-hxd-muted max-w-[420px] mx-auto">
            Choose <strong>{minAnchors} to {maxAnchors}</strong> articles above as the
            anchors of today's brief, then click <em>Generate red thread</em>.
            Wingman will weave them into John's voice.
          </p>
        </div>
      ) : (
        <>
          <textarea
            className={
              drafting
                ? 'hxd-input min-h-[160px] opacity-60'
                : 'hxd-input min-h-[160px]'
            }
            rows={7}
            value={thesis}
            onChange={onChange}
            placeholder="Click Generate red thread once you've picked your anchors."
            readOnly={drafting}
          />
          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px]">
            <span
              className={
                inRange
                  ? 'text-hxd-ok'
                  : wordCount === 0
                    ? 'text-hxd-muted'
                    : 'text-hxd-warn'
              }
            >
              {wordCount} words {wordCount > 0 && (inRange ? '· in range' : '· out of 60–90')}
            </span>
            {edited && <span className="text-hxd-muted">Edited by you</span>}
            {linksStale && (
              <span className="text-hxd-warn">
                In-copy links may be out of date — regenerate red thread to refresh them.
              </span>
            )}
          </div>
        </>
      )}

      {draftError && (
        <div className="mt-3 text-[12px] text-hxd-err bg-red-50 border border-red-200 rounded p-2">
          {draftError}
        </div>
      )}
    </section>
  );
}

function ThreadQualityPill({ quality }) {
  const map = {
    strong: { label: 'Strong thread', cls: 'bg-hxd-ok/15 text-hxd-ok border-hxd-ok/40' },
    adequate: {
      label: 'Adequate',
      cls: 'bg-hxd-warn/15 text-hxd-warn border-hxd-warn/40',
    },
    no_thread_found: {
      label: 'No clear thread',
      cls: 'bg-hxd-err/15 text-hxd-err border-hxd-err/40',
    },
  };
  const meta = map[quality];
  if (!meta) return null;
  return (
    <span
      className={`inline-flex items-center font-display text-[10px] font-bold tracking-display-sm uppercase px-2 py-0.5 border rounded-[2px] ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}

function ArticleReference({
  articles,
  anchorUrls,
  onToggleAnchor,
  disabled,
  minAnchors,
  maxAnchors,
}) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [allOpen, setAllOpen] = useState(false);

  if (articles.length === 0) {
    return (
      <section className="hxd-card">
        <div className="hxd-card-header">Pick your anchor articles</div>
        <p className="text-[12px] text-hxd-muted">
          No shipping articles in this edition.
        </p>
      </section>
    );
  }

  function toggle(id) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (allOpen) {
      setExpandedIds(new Set());
      setAllOpen(false);
    } else {
      setExpandedIds(new Set(articles.map((a) => String(a._id))));
      setAllOpen(true);
    }
  }

  const anchorCount = anchorUrls.size;
  const inRange = anchorCount >= minAnchors && anchorCount <= maxAnchors;

  return (
    <section className="hxd-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="hxd-card-header mb-0">
          Pick your anchor articles{' '}
          <span className="font-sans font-normal normal-case tracking-normal text-hxd-muted text-[11px]">
            ({articles.length} stories)
          </span>
        </div>
        <button
          type="button"
          onClick={toggleAll}
          className="font-display text-[10px] font-bold tracking-display-sm uppercase text-hxd-muted hover:text-hxd-orange transition-colors"
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      <p className="text-[12px] text-hxd-muted mb-3">
        Pick <strong>{minAnchors} to {maxAnchors}</strong> articles to anchor today's
        red thread. Wingman will build the thesis around them.{' '}
        <span
          className={
            inRange
              ? 'text-hxd-ok font-semibold'
              : anchorCount === 0
                ? 'text-hxd-muted'
                : 'text-hxd-warn font-semibold'
          }
        >
          {anchorCount} / {maxAnchors} selected
          {inRange && ' · ready'}
          {anchorCount > 0 && anchorCount < minAnchors && ` · need ${minAnchors - anchorCount} more`}
          {anchorCount > maxAnchors && ` · remove ${anchorCount - maxAnchors}`}
        </span>
      </p>

      <ul className="space-y-2">
        {articles.map((a, idx) => {
          const id = String(a._id);
          const open = expandedIds.has(id);
          const url = a.source?.url || '';
          const isAnchor = !!url && anchorUrls.has(url);
          const palette = CATEGORY_PALETTE[a.category] || CATEGORY_FALLBACK;
          return (
            <li
              key={id}
              className={
                isAnchor
                  ? 'border-2 rounded-[3px] overflow-hidden bg-white'
                  : 'border-2 border-hxd-border-soft rounded-[3px] overflow-hidden bg-white'
              }
              style={isAnchor ? { borderColor: '#F08F36' } : undefined}
            >
              <div className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-black/[0.03] transition-colors">
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  aria-expanded={open}
                  className="flex-1 flex items-center gap-3 text-left min-w-0"
                >
                  <span className="font-display text-[10px] font-bold text-hxd-orange w-6 text-right">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span
                    className="font-display text-[9px] font-bold uppercase tracking-display-sm px-1.5 py-0.5 rounded-[2px] text-white shrink-0"
                    style={{ backgroundColor: palette.bar }}
                  >
                    {a.category || '—'}
                  </span>
                  <span className="flex-1 text-[13px] font-semibold text-hxd-ink truncate">
                    {a.headline || '(no headline)'}
                  </span>
                  {a.source?.displayName && (
                    <span className="text-[11px] text-hxd-muted truncate max-w-[160px] hidden sm:inline">
                      {a.source.displayName}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => onToggleAnchor?.(url)}
                  disabled={disabled || !url}
                  aria-pressed={isAnchor}
                  title={
                    !url
                      ? 'No source URL — cannot anchor'
                      : isAnchor
                        ? 'Remove from anchors'
                        : 'Use as anchor'
                  }
                  className={
                    isAnchor
                      ? 'shrink-0 font-display text-[10px] font-bold uppercase tracking-display-sm px-2 py-1 rounded-[2px] bg-hxd-orange text-white border border-hxd-orange disabled:opacity-50'
                      : 'shrink-0 font-display text-[10px] font-bold uppercase tracking-display-sm px-2 py-1 rounded-[2px] bg-white text-hxd-muted border border-hxd-border hover:border-hxd-orange hover:text-hxd-orange disabled:opacity-50 disabled:hover:border-hxd-border disabled:hover:text-hxd-muted'
                  }
                >
                  {isAnchor ? '⛓ Anchor' : 'Anchor'}
                </button>
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  aria-label={open ? 'Collapse article' : 'Expand article'}
                  className="shrink-0 text-hxd-muted hover:text-hxd-ink px-1"
                >
                  <span
                    aria-hidden="true"
                    className="font-mono text-[14px] leading-none transition-transform duration-150 inline-block"
                    style={{
                      transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                  >
                    ▾
                  </span>
                </button>
              </div>
              {open && (
                <div
                  className="px-3 pb-3 pt-1 text-[12px] text-hxd-ink space-y-2"
                  style={{ borderTop: `1px solid ${palette.track}` }}
                >
                  {a.keyTakeaway && (
                    <p
                      className="italic text-[13px] border-l-2 pl-3 leading-relaxed"
                      style={{ borderColor: palette.bar }}
                    >
                      {a.keyTakeaway}
                    </p>
                  )}
                  {a.summary && (
                    <p className="whitespace-pre-line leading-relaxed text-hxd-ink">
                      {a.summary}
                    </p>
                  )}
                  {a.source?.url && (
                    <a
                      href={a.source.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block text-[11px] text-hxd-orange hover:underline break-all"
                    >
                      Open source ↗
                    </a>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
